import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../config/firebase';
import FileUpload from '../ui/FileUpload';
import {
  XMarkIcon,
  PhotoIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CheckIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { showError } from '../../utils/toast';

const ServiceForm = ({ service, onSubmit, onCancel, isLoading }) => {
  const {t} = useTranslation();
  const { apiCall } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    price: '',
    original_price: '',
    discount_percentage: '',
    is_on_sale: false,
    sale_start_date: '',
    sale_end_date: '',
    duration: '60',
    pet_types: ['dog'],
    available_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    start_time: '09:00',
    end_time: '17:00',
    assigned_employees: [],
    max_bookings_per_slot: '1',
    buffer_time_before: '15',
    buffer_time_after: '15',
    advance_booking_days: '30',
    cancellation_policy: 'Cancellation allowed up to 24 hours before appointment',
    is_active: true,
  });

  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [petTypes, setPetTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const daysOfWeek = [
    { value: 'monday', label: t("company.services.form.days.monday") },
    { value: 'tuesday', label: t("company.services.form.days.tuesday") },
    { value: 'wednesday', label: t("company.services.form.days.wednesday") },
    { value: 'thursday', label: t("company.services.form.days.thursday") },
    { value: 'friday', label: t("company.services.form.days.friday") },
    { value: 'saturday', label: t("company.services.form.days.saturday") },
    { value: 'sunday', label: t("company.services.form.days.sunday") }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

const formatTime = (time) => {
  if (!time) return '';

  if (typeof time === 'string' && time.includes('T')) {
    return time.split('T')[1].substring(0, 5);
  }

  return time?.substring(0, 5) || '';
};

useEffect(() => {
  if (service) {
    setFormData({
      name: service.name || '',
      description: service.description || '',
      category_id: service.category_id || '',
      pet_types: service.pet_types || [],
      price: service.price?.toString() || '',
      original_price: service.original_price?.toString() || '',
      discount_percentage: service.discount_percentage?.toString() || '',
      is_on_sale: service.is_on_sale || false,
      sale_start_date: service.sale_start_date || '',
      sale_end_date: service.sale_end_date || '',
      duration: service.duration?.toString() || '60',
      available_days: service.available_days || [],
      start_time: formatTime(service.start_time) || '09:00',
      end_time: formatTime(service.end_time) || '17:00',
      assigned_employees: service.assigned_employees || [],
      max_bookings_per_slot: service.max_bookings_per_slot?.toString() || '1',
      buffer_time_before: service.buffer_time_before?.toString() || '15',
      buffer_time_after: service.buffer_time_after?.toString() || '15',
      advance_booking_days: service.advance_booking_days?.toString() || '7',
      cancellation_policy: service.cancellation_policy || '',
      is_active: service.is_active !== undefined ? service.is_active : true,
      image_id: service.image_id || ''
    });

    if (service.image_url) {
      setUploadedImages([
        {
          id: service.image_id,
          url: service.image_url
        }
      ]);
    }
  }
}, [service]);

  const loadInitialData = async () => {
    try {
      const [cats, emps, pets] = await Promise.all([
        apiCall('/companies/service-categories'),
        apiCall('/companies/employees'),
        apiCall('/companies/pet-types')
      ]);

      if (cats?.success) setCategories(cats.data);
      if (emps?.employees) setEmployees(emps.employees);
      if (pets?.success) setPetTypes(pets.data);
    } catch (err) {
      console.error("Failed to load form metadata", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleArrayItem = (name, itemValue) => {
    setFormData(prev => {
      const current = prev[name];
      const updated = current.includes(itemValue)
        ? current.filter(i => i !== itemValue)
        : [...current, itemValue];
      return { ...prev, [name]: updated };
    });
  };

  const handleImageUpload = async (files) => {
    if (!files?.length) return;
    setIsUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', files[0]);

      const token = await auth.currentUser?.getIdToken();
      const response = await fetch('/api/v1/uploads/temp', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadFormData
      });
      
      const resData = await response.json();
      if (resData.success) {
        setFormData(prev => ({ ...prev, image_id: resData.data.file_id }));
        setUploadedImages([{
          id: resData.data.file_id,
          url: resData.data.file_url
        }]);
      }
    } catch (error) {
      showError(t("company.services.form.errors.upload_failed"));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = t("company.services.form.errors.required");
    if (!formData.category_id) newErrors.category_id = 'Required';
    if (!formData.price) newErrors.price = 'Required';
    if (formData.pet_types.length === 0) newErrors.pet_types = t("company.services.form.errors.select_one");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const finalData = {
      ...formData,
      price: parseFloat(formData.price),
      duration: parseInt(formData.duration),
      start_time: formData.start_time ? `${formData.start_time}:00` : null,
      end_time: formData.end_time ? `${formData.end_time}:00` : null, 
      max_bookings_per_slot: parseInt(formData.max_bookings_per_slot),
      buffer_time_before: parseInt(formData.buffer_time_before),
      buffer_time_after: parseInt(formData.buffer_time_after),
      advance_booking_days: parseInt(formData.advance_booking_days),
    };

    onSubmit(finalData);
  };

// ... (остальной код импортов и логики остается прежним)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-zinc-900/60 backdrop-blur-sm">
      {/* max-h-[90vh] — чтобы окно не упиралось в края экрана
        flex-col — чтобы разделить заголовок, контент и футер
      */}
      <div className="relative flex flex-col w-full max-w-5xl bg-white max-h-[90vh] shadow-2xl rounded-3xl overflow-hidden border border-zinc-100 transition-all">
        
        {/* FIXED HEADER: Всегда сверху */}
        <div className="flex-none px-8 py-6 border-b border-zinc-100 flex justify-between items-center bg-white/80 backdrop-blur-md z-10">
          <div>
            <h3 className="text-xl font-bold text-zinc-900 tracking-tight">
              {service ? t("company.services.form.title_edit") : t("company.services.form.title_create")}
            </h3>
            <p className="text-sm text-zinc-500">{t("company.services.form.subtitle")}</p>
          </div>
          <button 
            onClick={onCancel} 
            className="group p-2 hover:bg-zinc-100 rounded-full transition-all"
          >
            <XMarkIcon className="w-6 h-6 text-zinc-400 group-hover:text-zinc-600 group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* SCROLLABLE CONTENT: Только эта часть прокручивается */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-8">
          <form id="service-form" onSubmit={onFormSubmit} className="space-y-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Левая колонка */}
              <div className="space-y-8">
                <SectionTitle icon={<TagIcon />} title={t("company.servicess.form.sections.details")} />
                
                <div className="space-y-5">
                  <Input label={t("company.services.form.labels.name")} name="name" value={formData.name} onChange={handleInputChange} error={errors.name} placeholder={t("company.services.form.placeholders.name")} />
                  
                  <div>
                    <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-[0.1em] mb-2 ml-1">{t("company.services.form.labels.description")}</label>
                    <textarea 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange} 
                      rows={4} 
                      className="w-full px-4 py-3 bg-zinc-50/50 border border-zinc-200 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all outline-none resize-none placeholder:text-zinc-300" 
                      placeholder={t("company.services.form.placeholders.description")}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-[0.1em] mb-2 ml-1">{t("company.services.form.labels.category")}</label>
                      <select name="category_id" value={formData.category_id} onChange={handleInputChange} className="w-full px-4 py-3 bg-zinc-50/50 border border-zinc-200 rounded-2xl focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none appearance-none transition-all cursor-pointer">
                        <option value="">{t("company.services.form.placeholders.category")}</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <Input label={t("company.services.form.labels.price")+ " USD *"} name="price" type="number" value={formData.price} onChange={handleInputChange} error={errors.price} placeholder="0.00" />
                  </div>
                </div>

                <div className="pt-4">
                  <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-[0.1em] mb-3 ml-1">{t("company.services.form.labels.image")}</label>
                  <FileUpload
                    onFilesSelected={handleImageUpload}
                    maxFiles={1}
                    uploadedFiles={uploadedImages}
                    onFileRemove={() => {
                      setUploadedImages([]);
                      setFormData(p => ({ ...p, image_id: '' }));
                    }}
                    isUploading={isUploadingImage}
                    className="border-2 border-dashed border-zinc-200 rounded-3xl hover:border-orange-400 hover:bg-orange-50/50 transition-all"
                  />
                </div>
              </div>

              {/* Правая колонка */}
              <div className="space-y-10">
                <div className="space-y-6">
                  <SectionTitle icon={<CalendarDaysIcon />} title={t("company.services.form.sections.availability")} />
                  
                  <div>
                    <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-[0.1em] mb-3 ml-1">{t("company.services.form.labels.pet_types")}</label>
                    <div className="flex flex-wrap gap-2">
                      {petTypes.map(type => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => toggleArrayItem('pet_types', type.id)}
                          className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                            formData.pet_types.includes(type.id)
                              ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-200 ring-4 ring-orange-500/10'
                              : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
                          }`}
                        >
                          {type.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-zinc-400 uppercase tracking-[0.1em] mb-3 ml-1">{t("company.services.form.labels.work_days")}</label>
                    <div className="grid grid-cols-7 gap-2">
                      {daysOfWeek.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleArrayItem('available_days', day.value)}
                          className={`h-11 rounded-xl flex items-center justify-center text-[11px] font-black transition-all border uppercase ${
                            formData.available_days.includes(day.value)
                              ? 'bg-zinc-900 border-zinc-900 text-white shadow-md'
                              : 'bg-zinc-50/50 border-zinc-200 text-zinc-400 hover:bg-zinc-100'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-5">
                    <Input label={t("company.services.form.labels.starts_at")} name="start_time" type="time" value={formData.start_time} onChange={handleInputChange} />
                    <Input label={t("company.services.form.labels.ends_at")} name="end_time" type="time" value={formData.end_time} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="pt-6 border-t border-zinc-100 space-y-6">
                  <SectionTitle icon={<ClockIcon />} title={t("company.services.form.sections.time_settings")} />
                  <div className="grid grid-cols-2 gap-5">
                    <Input label={t("company.services.form.labels.duration")} name="duration" type="number" value={formData.duration} onChange={handleInputChange} />
                    <Input label={t("company.services.form.labels.buffer_after")} name="buffer_time_after" type="number" value={formData.buffer_time_after} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>

        {/* FIXED FOOTER: Всегда снизу */}
        <div className="flex-none px-8 py-6 border-t border-zinc-100 bg-zinc-50/50 backdrop-blur-md flex items-center justify-between">
          <label className="flex items-center cursor-pointer group">
            <div className="relative">
              <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="sr-only" />
              <div className={`w-11 h-6 rounded-full transition-all duration-300 ${formData.is_active ? 'bg-green-500 shadow-inner' : 'bg-zinc-300'}`}></div>
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-md transition-transform duration-300 ${formData.is_active ? 'translate-x-5' : ''}`}></div>
            </div>
            <span className={`ml-3 text-sm font-bold transition-colors ${formData.is_active ? 'text-green-600' : 'text-zinc-400'}`}>
              {formData.is_active ? t("company.services.form.labels.status_active") : t("company.services.form.labels.status_disable")}
            </span>
          </label>

          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-sm font-bold text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              {t("company.services.form.buttons.cancel")}
            </button>
            <button
              type="submit"
              form="service-form" // Привязываем кнопку к форме, так как она вне тега <form>
              disabled={isLoading}
              className="px-10 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-200 text-white text-sm font-black rounded-2xl shadow-xl shadow-orange-200 transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:transform-none"
            >
              {isLoading ? t("company.services.form.buttons.saving") : service ? t("company.services.form.buttons.update") : t("company.services.form.buttons.save")}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// Reusable Sub-components for cleaner look
const SectionTitle = ({ icon, title }) => (
  <div className="flex items-center space-x-2 text-zinc-800">
    <span className="p-1.5 bg-orange-50 text-orange-600 rounded-lg">{React.cloneElement(icon, { className: 'w-4 h-4' })}</span>
    <h4 className="text-sm font-bold uppercase tracking-widest">{title}</h4>
  </div>
);

const Input = ({ label, error, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">{label}</label>
    <input
      {...props}
      className={`w-full px-4 py-3 bg-zinc-50 border rounded-xl focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all outline-none ${
        error ? 'border-red-400 ring-red-100' : 'border-zinc-200'
      }`}
    />
    {error && <p className="text-[10px] text-red-500 mt-1 font-bold uppercase tracking-tighter">{error}</p>}
  </div>
);

export default ServiceForm;